import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape, FormattedMessage } from 'react-intl';

import { graphql, gql, compose } from 'react-apollo';
import { Route, Redirect, Switch, Link } from 'react-router-dom';

import { Step, Stepper, StepLabel } from 'material-ui/Stepper';
import MenuItem from 'material-ui/MenuItem';
import RaisedButton from 'material-ui/RaisedButton';

import Loader from '../UI/Loader';
import HomePage from './HomePage';

import RegList from '../Components/RegList';
import RegisterParticipants from '../Components/RegisterParticipants';

import hourglass from './hourglass.svg';
import Divider from 'material-ui/Divider';

const profileWithRegListsQuery = gql`
    query ProfileWithRegListsQuery {
        me {
            _id
            name
            email
            role
            townName
            regLists {
                _id
                town {
                    _id
                }
                tournament {
                    _id
                }
                townName
                population
                orgHeadName
                address
                addressee
                orgHeadPosition
                juryHeadName
                juryHeadPosition
                email
                phone
                invitations
                pDate
                mDate
                tDate
                vDate
                pPapersNumber
                mPapersNumber
                tPapersNumber
                vPapersNumber
                participantsNumber
                sentPPapersNumber
                sentMPapersNumber
                sentTPapersNumber
                sentVPapersNumber
                sentParticipantsNumber
                payment
                paymentData
                rulesAreFollowed
                ruleBreakersList
                comments
                lastGrade
                season
                sentForMarking
                participantDrafts {
                    _id
                    name
                    gender
                    grade
                    school
                    email
                    paperDrafts {
                        _id
                        round
                        level
                        filename
                        file
                        pages
                    }
                }
            }
        }
        tournaments {
            _id
            number
            p {
                junior {
                    name
                    itemOf
                }
                senior {
                    name
                    itemOf
                }
            }
            m {
                junior {
                    name
                    itemOf
                }
                senior {
                    name
                    itemOf
                }
            }
            t {
                junior {
                    name
                    itemOf
                }
                senior {
                    name
                    itemOf
                }
            }
            v {
                junior {
                    name
                    itemOf
                }
                senior {
                    name
                    itemOf
                }
            }
        }
    }
`;

const sendRegListForMarkingMutation = gql`
    mutation SendRegListForMarkingMutation($regListId: ID!) {
        sendRegListForMarking(regListId: $regListId) {
            _id
            sentForMarking
        }
    }
`;

const firstTournamentYear = 1978;
const currentYear = new Date().getFullYear();
const currentMonth = new Date().getMonth();

function hasRound(round, tournament) {
    const r = tournament[round];

    return r && r.junior && r.junior.length && r.senior && r.senior.length;
}

function getLastTournament(tournaments) {
    const [lastTournament] = [...tournaments].sort((a, b) => b.number - a.number);

    return lastTournament;
}

function getCurrentSeason(tournaments) {
    const lastTournament = getLastTournament(tournaments);

    if (
        firstTournamentYear + lastTournament.number === currentYear &&
        hasRound('p', lastTournament) &&
        hasRound('m', lastTournament)
    ) {
        return 'fall';
    }

    if (currentMonth >= 9) {
        return 'none';
    }

    if (hasRound('t', lastTournament) && hasRound('v', lastTournament)) {
        return 'spring';
    }

    if (currentMonth >= 3) {
        return 'none';
    }

    return 'fall';
}

function getCurrentRegList(regLists, tournaments) {
    const lastTournamentId = getLastTournament(tournaments)._id;
    const currentSeason = getCurrentSeason(tournaments);

    return (
        regLists.filter(
            regList => regList.tournament._id === lastTournamentId && regList.season === currentSeason
        )[0] || null
    );
}

class OrgHomePage extends Component {
    static propTypes = {
        intl: intlShape.isRequired,
        data: PropTypes.shape({
            loading: PropTypes.bool.isRequired,
            me: PropTypes.object,
            tournaments: PropTypes.array,
        }).isRequired,
        location: PropTypes.object.isRequired,
        history: PropTypes.object.isRequired,
    };

    handleRegListSave = () => {
        this.props.history.push('/participants');
        this.props.data.refetch();
    };

    handleParticipantsSave = () => {
        this.props.history.push('/send');
        this.props.data.refetch();
    };

    handleSendClick = async () => {
        const {
            data: { me, tournaments },
            sendRegListForMarking,
        } = this.props;

        await sendRegListForMarking({
            variables: {
                regListId: getCurrentRegList(me.regLists, tournaments)._id,
            },
        });

        this.props.data.refetch();
    };

    render() {
        const {
            intl: { formatMessage },
            data: { loading, me, tournaments },
            history,
            location: { pathname },
        } = this.props;

        if (loading) {
            return <Loader centerX />;
        }

        if (!loading && !me) {
            return <Redirect to="/" />;
        }

        const currentSeason = getCurrentSeason(tournaments);

        if (currentSeason === 'none') {
            return (
                <HomePage>
                    <h2 style={{ 'text-align': 'center' }}>
                        <img alt="" src={hourglass} style={{ height: '128px', width: '128px' }} />
                        <br />
                        <FormattedMessage id="OrgHomePage.wait" />
                    </h2>
                </HomePage>
            );
        }

        const currentRegList = !loading && getCurrentRegList(me.regLists, tournaments);
        const editable = !currentRegList || !currentRegList.sentForMarking;

        const stepIndex = pathname === '/participants' ? 1 : pathname === '/send' ? (editable ? 2 : 3) : 0;

        return (
            <HomePage
                menuItems={
                    <Fragment>
                        <MenuItem onClick={() => history.push('/')}>
                            <FormattedMessage id="OrgHomePage.RegList" />
                        </MenuItem>
                        <MenuItem onClick={() => history.push('/participants')} disabled={!currentRegList}>
                            <FormattedMessage id="OrgHomePage.Participants list" />
                        </MenuItem>
                        <MenuItem
                            onClick={() => history.push('/send')}
                            disabled={!currentRegList || !currentRegList.participantDrafts.length}
                        >
                            <FormattedMessage id="OrgHomePage.Send for marking" />
                        </MenuItem>
                        <Divider />
                    </Fragment>
                }
            >
                <Stepper activeStep={stepIndex}>
                    <Step>
                        <StepLabel>
                            <FormattedMessage id="OrgHomePage.Fill in RegList" />
                        </StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>
                            <FormattedMessage id="OrgHomePage.Upload papers" />
                        </StepLabel>
                    </Step>
                    <Step>
                        <StepLabel>
                            <FormattedMessage id="OrgHomePage.Send for marking" />
                        </StepLabel>
                    </Step>
                </Stepper>

                <Switch>
                    <Route
                        exact
                        path="/"
                        render={() => (
                            <RegList
                                regList={currentRegList}
                                season={currentSeason}
                                email={me.email}
                                townName={me.townName}
                                onSave={this.handleRegListSave}
                                editable={editable}
                            />
                        )}
                    />
                    <Route
                        exact
                        path="/participants"
                        render={() => (
                            <RegisterParticipants
                                townName={me.townName}
                                season={currentSeason}
                                regListId={currentRegList && currentRegList._id}
                                participants={currentRegList && currentRegList.participantDrafts}
                                onSave={this.handleParticipantsSave}
                                editable={editable}
                            />
                        )}
                    />
                    <Route
                        exact
                        path="/send"
                        render={() =>
                            editable ? (
                                <div>
                                    <h3>
                                        <FormattedMessage id="OrgHomePage.upload success title" />
                                    </h3>
                                    <FormattedMessage id="OrgHomePage.upload success note" />
                                    <br />
                                    <FormattedMessage id="OrgHomePage.upload success note 2" />
                                    <br />
                                    <br />
                                    <FormattedMessage
                                        id="OrgHomePage.upload success"
                                        values={{
                                            regList: (
                                                <Link to="/">
                                                    <FormattedMessage id="OrgHomePage.regList" />
                                                </Link>
                                            ),
                                            viewParticipants: (
                                                <Link to="/participants">
                                                    <FormattedMessage id="OrgHomePage.viewParticipants" />
                                                </Link>
                                            ),
                                        }}
                                    />
                                    <br />
                                    <br />
                                    <br />
                                    <RaisedButton
                                        action="submit"
                                        primary
                                        label={formatMessage({ id: 'OrgHomePage.Send for marking' })}
                                        onClick={this.handleSendClick}
                                    />
                                </div>
                            ) : (
                                <h3>
                                    <FormattedMessage id="OrgHomePage.send success" />
                                </h3>
                            )
                        }
                    />
                    <Redirect to="/" />
                </Switch>
            </HomePage>
        );
    }
}

export default compose(
    graphql(profileWithRegListsQuery),
    graphql(sendRegListForMarkingMutation, { name: 'sendRegListForMarking' }),
    injectIntl
)(OrgHomePage);



// WEBPACK FOOTER //
// ./src/Pages/OrgHomePage.js